import { useNavigate } from 'react-router-dom';
import Btn, { type BtnProps } from './Btn';
import { IconAngleUp } from '@icons';
import { useOptionalNavigationGuard } from '../../../contexts/NavigationGuardContext';

interface BackButtonProps {
    /** When set, navigates to this route (respects unsaved-change guards when available). */
    to?: string;
    /** Used when browser history is empty and `to` is not set. Defaults to `/`. */
    fallbackTo?: string;
    /** When set, runs instead of route/history navigation. */
    onClick?: () => void;
    buttonClasses?: string;
    buttonText?: string;
    ariaLabel?: string;
    variant?: BtnProps['variant'];
}

const BackButton = ({
    to,
    fallbackTo = '/',
    onClick,
    buttonClasses,
    buttonText = 'Back',
    ariaLabel,
    variant = 'secondary-outlined',
}: BackButtonProps) => {
    const navigate = useNavigate();
    const guard = useOptionalNavigationGuard();

    const handleClick = () => {
        if (onClick) {
            onClick();
            return;
        }

        if (to) {
            if (guard) {
                guard.tryNavigate(to);
            } else {
                navigate(to);
            }
            return;
        }

        if (guard) {
            guard.tryGoBack(fallbackTo);
            return;
        }

        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate(fallbackTo);
    };

    return (
        <Btn
            variant={variant}
            buttonText={buttonText}
            buttonIcon={<IconAngleUp iconClasses="back-button-icon" />}
            buttonIconPosition="left"
            buttonOnClick={handleClick}
            ariaLabel={ariaLabel ?? buttonText}
            buttonClasses={['back-button', buttonClasses].filter(Boolean).join(' ')}
        />
    );
};

export default BackButton;
